import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlarmClock, Banknote, ChevronRight, DoorOpen, FileKey, Plus, Search, TriangleAlert, X } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { StatusPill } from '@/components/StatusPill';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import { useDebounced } from '@/hooks/useDebounced';
import { Chip } from '@/pages/catalog/shared';
import { LEASE_STATUSES, Lease, LeaseStatus, LeasesSummary, realEstateService } from '@/services/real-estate.service';
import LeaseFormModal, { chequesLabel } from './LeaseFormModal';
import LeaseDrawer from './LeaseDrawer';
import {
  Kpi, LEASE_PHASE_LABEL, LEASE_PHASE_VARIANT, LEASE_STATUS_LABEL, formatDay, formatPrice, periodShort, useRealEstateAccess,
} from './shared';

const LIMIT = 25;
const EXPIRING_DAYS = 60;

function endHint(l: Lease): { text: string; tone: string } | null {
  if (l.status !== 'active') return null;
  if (l.phase === 'upcoming') return { text: 'Not started', tone: 'text-info' };
  if (l.daysToEnd < 0) return { text: `${Math.abs(l.daysToEnd)}d past end`, tone: 'text-danger' };
  if (l.daysToEnd <= EXPIRING_DAYS) return { text: l.daysToEnd === 0 ? 'Ends today' : `${l.daysToEnd}d left`, tone: 'text-warning' };
  return null;
}

export default function LeasesPage() {
  const t = useLabels();
  const companyCurrency = useCompanyCurrency();
  const { contactLinks } = useRealEstateAccess();
  const [params, setParams] = useSearchParams();
  const plural = t('leases');
  const noun = t('leases', 'singular');

  const [status, setStatus] = useState<LeaseStatus | ''>('');
  const [expiring, setExpiring] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 300);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Lease[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [summary, setSummary] = useState<LeasesSummary | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const reqId = useRef(0);

  const openLease = params.get('lease');
  const filtered = status !== '' || expiring || !!q;

  // ?new=1 (from other pages) opens the form once, then drops the flag.
  useEffect(() => {
    if (params.get('new') !== '1') return;
    setNewOpen(true);
    const p = new URLSearchParams(params);
    p.delete('new');
    setParams(p, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setError(false);
    try {
      const r = await realEstateService.listLeases({
        status: expiring ? undefined : status || undefined,
        expiringWithin: expiring ? EXPIRING_DAYS : undefined,
        q: q || undefined,
        page,
        limit: LIMIT,
      });
      if (id !== reqId.current) return;
      setItems(r.items);
      setTotal(r.total);
    } catch {
      if (id === reqId.current) setError(true);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [status, expiring, q, page]);

  const loadSummary = useCallback(() => {
    realEstateService.leasesSummary().then(setSummary).catch(() => { /* tiles keep their last numbers */ });
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setPage(1); }, [q]);

  const refresh = () => { void load(); loadSummary(); };

  const setDrawer = useCallback((uid: string | null) => {
    const p = new URLSearchParams(params);
    if (uid) p.set('lease', uid);
    else p.delete('lease');
    setParams(p, { replace: true });
  }, [params, setParams]);

  const pickStatus = (s: LeaseStatus | '') => { setStatus(s); setExpiring(false); setPage(1); };
  const toggleExpiring = () => { setExpiring((e) => !e); setStatus(''); setPage(1); };
  const clearFilters = () => { setStatus(''); setExpiring(false); setQuery(''); setPage(1); };

  const cur = summary?.currency ?? companyCurrency;

  return (
    <>
      <Topbar
        title={plural}
        breadcrumb={`${t('operations')} / ${plural}`}
        description="Tenancy contracts, rent and renewals."
        right={<Button iconLeft={<Plus size={15} />} onClick={() => setNewOpen(true)}>New {noun.toLowerCase()}</Button>}
      />

      <div className="w-full space-y-5 px-4 py-6 sm:px-7 sm:py-7">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Kpi label={`Active ${plural.toLowerCase()}`} value={summary?.active} icon={<FileKey size={16} />} tone="gold"
            hint={summary ? `${summary.vacantForRent} ${summary.vacantForRent === 1 ? 'unit' : 'units'} vacant for rent` : undefined} />
          <Kpi label="Annual rent roll" value={summary ? formatPrice(summary.annualRentRoll, cur) : undefined} icon={<Banknote size={16} />} tone="green"
            hint={`Active ${plural.toLowerCase()} in ${cur}, per year`} />
          <Kpi label={`Ending in ${EXPIRING_DAYS} days`} value={summary?.expiring60} icon={<AlarmClock size={16} />} tone="amber"
            hint={summary ? `${summary.expiring30} within 30 days · ${summary.expiring90} within 90` : undefined}
            onClick={toggleExpiring} active={expiring} />
          <Kpi label="Past end date" value={summary?.pastEndDate} icon={<TriangleAlert size={16} />} tone={summary && summary.pastEndDate > 0 ? 'red' : 'blue'}
            hint="Still active — renew or end them" />
        </div>

        <div className="rounded-2xl border border-border bg-surface shadow-soft">
          <div className="flex flex-col gap-3 border-b border-border p-4 lg:flex-row lg:items-center">
            <div className="w-full lg:max-w-xs">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tenant, unit or registration no."
                iconLeft={<Search size={15} />}
                iconRight={query ? <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="hover:text-text-primary"><X size={14} /></button> : undefined}
                aria-label={`Search ${plural.toLowerCase()}`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
              <Chip active={status === '' && !expiring} onClick={() => pickStatus('')}>All</Chip>
              {LEASE_STATUSES.map((s) => (
                <Chip key={s} active={status === s && !expiring} onClick={() => pickStatus(s)}>{LEASE_STATUS_LABEL[s]}</Chip>
              ))}
              <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
              <Chip active={expiring} onClick={toggleExpiring}><AlarmClock size={13} /> Ending in {EXPIRING_DAYS} days</Chip>
            </div>
          </div>

          {error ? (
            <LoadErrorState what={plural.toLowerCase()} onRetry={() => void load()} bare />
          ) : loading && items.length === 0 ? (
            <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="p-4">
              {filtered ? (
                <NoMatchesState onClear={clearFilters} />
              ) : (
                <EmptyState
                  icon={<FileKey size={28} />}
                  title={`No ${plural.toLowerCase()} yet`}
                  description="Add a tenancy contract to a unit. The unit shows as rented while it’s active, and renewing takes one click."
                  action={<Button iconLeft={<Plus size={15} />} onClick={() => setNewOpen(true)}>New {noun.toLowerCase()}</Button>}
                />
              )}
            </div>
          ) : (
            <div className={clsx('transition-opacity', loading && 'opacity-60')}>
              <div className="hidden grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_120px_20px] gap-3 border-b border-border bg-surface-alt px-4 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted md:grid">
                <span>Tenant</span><span>Unit</span><span>Term</span><span>Rent</span><span>Status</span><span />
              </div>
              <ul className="divide-y divide-border">
                {items.map((l) => {
                  const hint = endHint(l);
                  return (
                    <li key={l.leaseUid}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setDrawer(l.leaseUid)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setDrawer(l.leaseUid); }}
                        className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 px-4 py-3 transition-colors hover:bg-surface-alt/60 focus-visible:bg-surface-alt focus-visible:outline-none md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_120px_20px] md:items-center"
                      >
                        <div className="min-w-0">
                          {l.tenant && contactLinks ? (
                            <Link to={`/contacts/${l.tenant.contactUid}`} onClick={(e) => e.stopPropagation()} className="block truncate text-[13.5px] font-semibold text-text-primary hover:text-primary hover:underline">{l.tenant.name}</Link>
                          ) : (
                            <div className="truncate text-[13.5px] font-semibold text-text-primary">{l.tenant?.name ?? 'Tenant removed'}</div>
                          )}
                          <div className="truncate text-2xs tabular-nums text-text-muted">{l.tenant?.phone ?? ''}{l.registrationNumber ? `${l.tenant?.phone ? ' · ' : ''}Reg. ${l.registrationNumber}` : ''}</div>
                        </div>
                        <div className="justify-self-end md:hidden">
                          <StatusPill status={l.phase} label={LEASE_PHASE_LABEL[l.phase]} variant={LEASE_PHASE_VARIANT[l.phase]} />
                        </div>
                        <div className="col-span-2 min-w-0 md:col-span-1">
                          <Link to={`/properties/${l.propertyUid}`} onClick={(e) => e.stopPropagation()} className="block truncate text-[13px] font-medium text-text-primary hover:text-primary hover:underline">
                            {l.unit?.label ?? 'Unit removed'}{l.property ? <span className="font-normal text-text-secondary"> · {l.property.name}</span> : null}
                          </Link>
                          {l.property?.community && <div className="truncate text-2xs text-text-muted">{[l.property.community, l.property.city].filter(Boolean).join(', ')}</div>}
                        </div>
                        <div className="col-span-2 min-w-0 text-xs md:col-span-1">
                          <div className="tabular-nums text-text-secondary">{formatDay(l.startDate, { day: 'numeric', month: 'short', year: '2-digit' })} – {formatDay(l.endDate, { day: 'numeric', month: 'short', year: '2-digit' })}</div>
                          {hint && <div className={clsx('text-2xs font-semibold', hint.tone)}>{hint.text}</div>}
                        </div>
                        <div className="col-span-2 min-w-0 text-xs md:col-span-1">
                          <div className="font-semibold tabular-nums text-text-primary">{formatPrice(l.rentAmount, l.currency)}<span className="font-normal text-text-muted">{periodShort(l.rentPeriod)}</span></div>
                          <div className="text-2xs text-text-muted">{chequesLabel(l.payments)}</div>
                        </div>
                        <div className="hidden md:block">
                          <StatusPill status={l.phase} label={LEASE_PHASE_LABEL[l.phase]} variant={LEASE_PHASE_VARIANT[l.phase]} />
                        </div>
                        <ChevronRight size={16} className="hidden text-text-muted md:block" />
                      </div>
                    </li>
                  );
                })}
              </ul>
              {total > LIMIT && <div className="border-t border-border px-4"><Pagination page={page} total={total} limit={LIMIT} onChange={setPage} /></div>}
            </div>
          )}
        </div>

        {summary && summary.vacantForRent > 0 && !filtered && items.length > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            <DoorOpen size={13} /> {summary.vacantForRent} {summary.vacantForRent === 1 ? 'unit is' : 'units are'} available for rent.
            <Link to="/properties" className="font-semibold text-primary hover:underline">See them</Link>
          </p>
        )}
      </div>

      <LeaseFormModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onSaved={(l) => { setNewOpen(false); refresh(); setDrawer(l.leaseUid); }}
      />
      <LeaseDrawer
        leaseUid={openLease}
        onClose={() => setDrawer(null)}
        onChanged={refresh}
        onOpen={(uid) => setDrawer(uid)}
      />
    </>
  );
}
